import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, RefreshCw, Sparkles, AlertTriangle, AlertOctagon } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const RISK_META = {
  normal: { color: 'cyan', icon: null, label: 'Normal' },
  warning: { color: 'amber', icon: AlertTriangle, label: 'Warning' },
  critical: { color: 'rose', icon: AlertOctagon, label: 'Critical' },
};

export default function ActivityDigestWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDigest = async () => {
    try {
      const r = await api.get('/admin/activity-digest');
      setData(r.data);
    } catch (e) {
      // Quietly skip — widget is admin-only and may 403 for managers without privileges
      if (e.response?.status !== 403) console.warn('digest fetch', e);
    } finally { setLoading(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/admin/activity-digest');
      setData(r.data);
      toast.success('Digest refreshed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { fetchDigest(); }, []);

  if (loading) return null;
  if (!data) return null;
  if ((data.total_events ?? 0) === 0 && data.model !== 'gpt-4o') return null;

  const rawRisk = data.risk_level || 'normal';
  const riskKey = (rawRisk === 'warning' || rawRisk === 'critical') ? rawRisk : 'normal';
  const risk = RISK_META[riskKey];
  const RiskIcon = risk.icon;
  const isAnomaly = riskKey !== 'normal';
  const isCritical = riskKey === 'critical';

  // Tone via classes (avoid dynamic Tailwind which is purged)
  const cardTone = isAnomaly
    ? (isCritical
        ? 'border-rose-300 dark:border-rose-900 bg-gradient-to-br from-rose-50/70 to-pink-50/40 dark:from-stone-900 dark:to-stone-900'
        : 'border-amber-300 dark:border-amber-900 bg-gradient-to-br from-amber-50/70 to-orange-50/40 dark:from-stone-900 dark:to-stone-900')
    : 'border-cyan-200 dark:border-cyan-900 bg-gradient-to-br from-cyan-50/60 to-sky-50/40 dark:from-stone-900 dark:to-stone-900';

  const iconWrap = isAnomaly
    ? (isCritical ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400')
    : 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400';

  return (
    <Card className={cardTone} data-testid="activity-digest-widget">
      <CardContent className="pt-4 space-y-2 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${iconWrap}`}>
              {RiskIcon ? <RiskIcon size={14} /> : <Activity size={14} />}
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold font-outfit flex items-center gap-2">
                Activity Digest · last 24h
                {isAnomaly && (
                  <span data-testid="risk-badge"
                    className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-bold tracking-wider ${
                      isCritical ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white'
                    }`}>
                    {risk.label}
                  </span>
                )}
              </h3>
              <p className="text-[10px] text-muted-foreground truncate">
                {data.total_events ?? 0} events · {data.unique_users ?? 0} active users · {data.model === 'gpt-4o' ? <span className="inline-flex items-center gap-0.5"><Sparkles size={9} /> GPT-4o</span> : 'fallback'}
                {data.from_cache && <span> · cached</span>}
              </p>
            </div>
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs shrink-0"
            onClick={refresh} disabled={refreshing} data-testid="refresh-digest">
            <RefreshCw size={12} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>

        {isAnomaly && data.risk_reason && (
          <div className={`text-xs p-2 rounded-md ${
            isCritical ? 'bg-rose-100/60 dark:bg-rose-900/20 text-rose-800 dark:text-rose-300' : 'bg-amber-100/60 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300'
          }`} data-testid="risk-reason">
            <strong>Why flagged:</strong> {data.risk_reason}
          </div>
        )}

        <p className="text-sm text-foreground leading-relaxed" data-testid="digest-narrative">
          {data.narrative || 'No narrative available.'}
        </p>
        {data.action_counts && Object.keys(data.action_counts).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {Object.entries(data.action_counts).slice(0, 6).map(([action, count]) => (
              <span key={action} className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 font-medium">
                {action.replace(/_/g, ' ')} · {count}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
